import { useEffect, useRef, useCallback } from "react";
import { useMediaPipe } from "./useMediaPipe";
import { sendSnapshot } from "../api/proctor";
import type { SnapshotResponse } from "../api/types/proctor.types";

interface UseProctoringOptions {
  user_id: string;
  user_exam_id: string;
  assessment_id: string;
  intervalMs?: number;
  onResult?: (result: SnapshotResponse) => void;
}

export function useProctoring(
  videoRef: React.RefObject<HTMLVideoElement>,
  options: UseProctoringOptions
) {
  const { user_id, user_exam_id, assessment_id, intervalMs = 5_000, onResult } = options;
  const { captureFrame } = useMediaPipe(videoRef);
  const sendingRef = useRef(false);

  const takeSnapshot = useCallback(async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    try {
      const { blob } = await captureFrame(true);
      if (!blob) return; // rejectReason available but proctoring just skips failed frames

      const result = await sendSnapshot({
        user_id,
        user_exam_id,
        assessment_id,
        image: blob,
      });
      onResult?.(result);
    } catch (err) {
      console.error("Proctoring snapshot error:", err);
    } finally {
      sendingRef.current = false;
    }
  }, [captureFrame, user_id, user_exam_id, assessment_id, onResult]);

  useEffect(() => {
    const id = setInterval(takeSnapshot, intervalMs);
    return () => clearInterval(id);
  }, [takeSnapshot, intervalMs]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        takeSnapshot();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [takeSnapshot]);

  return { takeSnapshot };
}
